import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, BarChart3 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";

const COLORS_STATUS = { Valid: "#059669", "Expiring Soon": "#D97706", Expired: "#DC2626" };
const CAT_COLORS = ["#01ADEF", "#0284C7", "#7C3AED", "#059669", "#D97706"];

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadCsv = async (kind) => {
    try {
      const res = await api.get(`/reports/${kind}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  if (!data) return <div className="p-8 text-gray-500">Loading analytics…</div>;

  const monthData = data.by_month.map((m) => ({ month: m.month.slice(2), scheduled: m.scheduled, passed: m.passed, failed: m.failed }));
  const catData = data.by_category.map((c) => ({ name: c.category.replace("_"," "), count: c.count }));

  return (
    <div className="p-6 md:p-8" data-testid="reports-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Insights</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Reports &amp; Analytics</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-inspections-csv" onClick={() => downloadCsv("inspections")} variant="outline" className="rounded-full border-gray-300">
            <Download className="w-4 h-4 mr-2" /> Inspections CSV
          </Button>
          <Button data-testid="export-certificates-csv" onClick={() => downloadCsv("certificates")} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
            <Download className="w-4 h-4 mr-2" /> Certificates CSV
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <TotalCard label="Total Assets" value={data.totals.assets} testid="total-assets" />
        <TotalCard label="Total Inspections" value={data.totals.inspections} testid="total-inspections" />
        <TotalCard label="Total Certificates" value={data.totals.certificates} testid="total-certificates" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Inspections over time */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">Inspections Over Time</h2>
              <p className="text-xs text-gray-500 mt-0.5">Last 6 months</p>
            </div>
            <BarChart3 className="w-5 h-5 text-[#01ADEF]" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12 }} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
              <Legend />
              <Line type="monotone" dataKey="scheduled" stroke="#01ADEF" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="passed" stroke="#059669" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="failed" stroke="#DC2626" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Cert status donut */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="mb-4">
            <h2 className="font-display text-lg font-bold tracking-tight">Certificate Status</h2>
            <p className="text-xs text-gray-500 mt-0.5">Current portfolio breakdown</p>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.cert_status} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={2}>
                {data.cert_status.map((entry) => (
                  <Cell key={`cs-${entry.name}`} fill={COLORS_STATUS[entry.name] || "#6B7280"} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Assets by category */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="mb-4">
            <h2 className="font-display text-lg font-bold tracking-tight">Assets by Category</h2>
            <p className="text-xs text-gray-500 mt-0.5">Distribution across your registry</p>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={catData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 12 }} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
              <Bar dataKey="count">
                {catData.map((entry, idx) => <Cell key={`cat-${entry.name || idx}`} fill={CAT_COLORS[idx % CAT_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pass/Fail breakdown */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="mb-4">
            <h2 className="font-display text-lg font-bold tracking-tight">Inspection Outcomes</h2>
            <p className="text-xs text-gray-500 mt-0.5">Pass / Fail / Pending</p>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {data.by_result.map((r) => (
              <div key={r.result} className="border border-gray-200 p-4 text-center">
                <div className={`text-3xl font-display font-bold ${
                  r.result === "pass" ? "text-[#059669]" : r.result === "fail" ? "text-[#DC2626]" : "text-gray-500"
                }`}>{r.count}</div>
                <div className="text-xs uppercase tracking-wider font-semibold text-gray-600 mt-1">{r.result}</div>
              </div>
            ))}
            {data.by_result.length === 0 && <div className="col-span-3 text-center text-gray-500 text-sm py-8">No data yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

const TotalCard = ({ label, value, testid }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">{label}</div>
    <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
  </div>
);
